import type { GetBrandRequestsResponse } from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { mapSortColumnToBackend } from "@/features/requests/utils/sortingUtils";
import { brandRequestsKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

import type { BrandRequestFilterId } from "../config/filterConfig";

interface UseBrandRequestsParams {
  brandId: number;
  limit: number;
  page: number;
  search?: string;
  sortDescriptor?: SortDescriptor;
  tab: BrandRequestFilterId;
}

export function useBrandRequests({
  brandId,
  limit,
  page,
  search,
  sortDescriptor,
  tab,
}: UseBrandRequestsParams): UseQueryResult<GetBrandRequestsResponse> {
  const sortBy = sortDescriptor?.column
    ? mapSortColumnToBackend(String(sortDescriptor.column))
    : undefined;
  const direction =
    sortDescriptor?.direction === "ascending"
      ? "asc"
      : sortDescriptor?.direction === "descending"
        ? "desc"
        : undefined;

  const queryParams: Record<string, unknown> = {
    limit,
    page,
    tab,
    ...(sortBy && { sortBy }),
    ...(direction && { direction }),
    ...(search && { search }),
  };

  return useQuery<GetBrandRequestsResponse>({
    enabled: brandId > 0,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      apiClient
        .url(`/brands/${brandId}/requests`)
        .query(queryParams)
        .get()
        .json<GetBrandRequestsResponse>(),
    queryKey: brandRequestsKeys.list(brandId, queryParams),
    staleTime: 30_000,
  });
}
